import { useState, useEffect } from 'react';
import { ArrowRight, DollarSign, Percent, TrendingUp } from 'lucide-react';

export default function ProfitSimulator() {
  const [cost, setCost] = useState<number>(100);
  const [margin, setMargin] = useState<number>(30);
  const [price, setPrice] = useState<number>(0);
  const [profit, setProfit] = useState<number>(0);

  useEffect(() => {
    const safeMargin = Math.min(margin, 99);
    const calculatedPrice = cost / (1 - safeMargin / 100);
    const calculatedProfit = calculatedPrice - cost;
    
    setPrice(calculatedPrice);
    setProfit(calculatedProfit);
  }, [cost, margin]);

  return (
    <section className="py-24 relative overflow-hidden bg-slate-50 border-b border-slate-200" aria-labelledby="simulator-heading">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 id="simulator-heading" className="text-3xl md:text-5xl font-extrabold text-slate-950 mb-6 tracking-tight">
              Veja o <span className="text-emerald-700">Lucro Real</span> Sumindo ou Aparecendo
            </h2>
            <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto font-normal">
              Simule agora mesmo. Muitos empreendedores acham que estão lucrando, mas na verdade estão apenas "trocando dinheiro".
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-stretch bg-white border border-slate-200 rounded-3xl md:rounded-4xl p-6 md:p-10 shadow-md overflow-hidden">
            <div className="flex flex-col justify-center">
              <div>
                <label htmlFor="cost-input" className="text-sm font-semibold text-slate-700 mb-3 md:mb-4 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" aria-hidden="true" />
                  Qual o seu custo total? (Matéria-prima + Impostos)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">R$</span>
                  <input
                    id="cost-input"
                    type="number"
                    value={cost}
                    onChange={(e) => setCost(Number(e.target.value))}
                    aria-label="Qual o seu custo total em reais"
                    className="w-full min-h-12 bg-slate-50 border border-slate-300 rounded-xl md:rounded-2xl py-3 md:py-4 pl-12 pr-4 text-xl md:text-2xl font-bold text-slate-950 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="bg-emerald-50/80 border border-emerald-200 rounded-2xl md:rounded-3xl p-6 md:p-8 flex flex-col justify-center relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-10 md:opacity-20 group-hover:opacity-30 transition-opacity">
                <TrendingUp className="w-16 h-16 md:w-24 md:h-24 text-emerald-600" aria-hidden="true" />
              </div>
              
              <div className="relative z-10 space-y-4 md:space-y-6">
                <div>
                  <span className="text-xs font-bold text-emerald-800 uppercase tracking-widest block mb-1">Preço Sugerido de Venda</span>
                  <div className="text-3xl md:text-5xl font-black text-slate-950 tracking-tight">
                    R$ {price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="h-px bg-emerald-200 w-full" aria-hidden="true"></div>

                <div>
                  <span className="text-xs font-bold text-emerald-800 uppercase tracking-widest block mb-1">Seu Lucro Líquido (No Bolso)</span>
                  <div className="text-2xl md:text-3xl font-extrabold text-emerald-700">
                    R$ {profit.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="pt-2 md:pt-4">
                  <div className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold text-emerald-900 bg-white/90 rounded-full px-4 py-2 border border-emerald-200 shadow-xs">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Margem sobre Preço
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 bg-white border border-slate-200 rounded-3xl md:rounded-4xl p-6 md:p-10 shadow-md overflow-hidden">
            <div>
              <label htmlFor="margin-input" className="text-sm font-semibold text-slate-700 mb-6 flex items-center gap-2">
                <Percent className="w-4 h-4 text-emerald-600" aria-hidden="true" />
                Qual margem de lucro você deseja?
              </label>
              <div className="relative pt-4 touch-pan-y py-3 min-h-12">
                <input
                  id="margin-input"
                  type="range"
                  min="1"
                  max="80"
                  value={margin}
                  onChange={(e) => setMargin(Number(e.target.value))}
                  aria-label="Selecione a porcentagem da margem de lucro desejada"
                  className="w-full h-3 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600 touch-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
                <div className="flex justify-between mt-4 text-xs md:text-sm text-slate-500 font-mono font-bold">
                  <span>5%</span>
                  <span>40%</span>
                  <span>80%</span>
                </div>
                <div className="absolute -top-6 right-0 text-3xl md:text-4xl font-black text-emerald-700">
                  {margin}%
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-600 text-sm mb-6 italic">
              *Este é apenas um exemplo simplificado. No app completo, você calcula impostos, taxas de cartão, frete e muito mais.
            </p>
            <button 
              aria-label="Navegar para a calculadora completa de precificação"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 min-h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-bold text-base transition-all group cursor-pointer shadow-md shadow-emerald-600/20 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              Quero a Calculadora Completa
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function CheckCircle2({ className }: { className?: string }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
      <path d="m9 12 2 2 4-4"/>
    </svg>
  );
}

